import { useGetTagValueQuery } from '@/api/tags.api'
import { QUALITY } from '@/constants/status'
import { TIME } from '@/constants/time'
import { useTagSubscription } from '@/hooks/useWebSocket'
import { useRealtimeStore } from '@/store/realtime.store'
import { formatValue } from '@/utils/format'

/** Quality → dot color class (purge-safe literals). */
const QUALITY_DOT: Record<string, string> = {
  [QUALITY.GOOD]: 'bg-success',
  [QUALITY.BAD]: 'bg-danger',
  [QUALITY.UNCERTAIN]: 'bg-warning',
}

export interface TagValueCellProps {
  tagId: string
  unit?: string | null
  dataType?: string
}

/**
 * Live value + quality dot for a tag. Prefers the realtime WebSocket value
 * (Stage 7) and falls back to a polled GET /tags/:id/value. IBM Plex Mono.
 */
export function TagValueCell({ tagId, unit, dataType }: TagValueCellProps) {
  // Subscribe to live values for this tag (ref-counted); falls back to polling.
  useTagSubscription(tagId)
  const live = useRealtimeStore((s) => s.tagValues.get(tagId))
  // Only poll the REST value while there's no live value for this tag.
  const { data, isLoading } = useGetTagValueQuery(tagId, {
    pollingInterval: TIME.TAG_REFRESH_MS,
    skip: Boolean(live),
  })

  const quality = live?.quality ?? data?.quality ?? 'unknown'
  const dotClass = QUALITY_DOT[quality] ?? 'bg-[var(--text-muted)]'

  // Resolve the value across the typed columns the API splits it into.
  let display: string
  if (live) {
    display = formatValue(live.value, unit ?? undefined)
  } else if (data) {
    // The API splits the reading across typed columns; pick by the tag's dataType.
    const raw =
      dataType === 'boolean' ? data.valueBool : dataType === 'string' ? data.valueText : data.value
    display = formatValue(raw, unit ?? undefined)
  } else {
    display = isLoading ? '…' : '—'
  }

  return (
    <span className="flex items-center gap-2">
      <span className={`h-2 w-2 shrink-0 rounded-full ${dotClass}`} title={quality} />
      <span className="font-mono tabular-nums text-text-primary">{display}</span>
    </span>
  )
}
